/**
 * HtmlCardMarkdownPre
 *
 * Drop-in Streamdown `pre` renderer that turns ```html-card fences into
 * interactive cards on surfaces that don't use the chat MessageMarkdown
 * (Spaces notes, Nexus agent chat, docs). Non-card code blocks fall back
 * to a plain <pre> with the incoming props, matching how the chat
 * renderer's own pre handlers behave.
 */

'use client'

import React from 'react'

import { HtmlCardHost, parseHtmlCardFence } from './HtmlCardHost'

const HTML_CARD_LANGUAGES = new Set([
  'html-card',
  'htmlcard',
  'losi-card',
  'interactive-card'
])

interface HtmlCardMarkdownPreProps {
  children?: React.ReactNode
  className?: string
  /** Send a submitted card payload as a reply, where the surface can. */
  onSubmit?: (payload: string) => void
  [key: string]: unknown
}

export function HtmlCardMarkdownPre({
  children,
  className,
  onSubmit,
  ...props
}: HtmlCardMarkdownPreProps) {
  const match = /language-([^\s]+)/.exec(className || '')
  const language = match ? match[1].toLowerCase() : ''

  if (HTML_CARD_LANGUAGES.has(language)) {
    // Child may be an intrinsic <code> or a surface's custom code
    // component — either way its children carry the raw fence text.
    const content = React.Children.toArray(children)
      .filter((child) => React.isValidElement(child))
      .map((child) =>
        React.isValidElement(child)
          ? String(
              (child.props as { children?: React.ReactNode }).children ?? ''
            )
          : ''
      )
      .join('')
    const parsed = parseHtmlCardFence(content)
    if (parsed.html) {
      return (
        <HtmlCardHost
          cardId={parsed.cardId || `html-card-${language}`}
          html={parsed.html}
          fallback={parsed.fallback}
          onSubmit={onSubmit}
        />
      )
    }
  }

  return (
    <pre className={className} {...props}>
      {children}
    </pre>
  )
}
